import type { InviteDto } from "@ied/shared-types";
import {
	createColumnHelper,
	getCoreRowModel,
	type TableOptions,
} from "@tanstack/react-table";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { useGetGroups } from "@/hooks/queries/use-get-groups";
import { useGetInvites } from "@/hooks/queries/use-get-invites";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { TanstackTable } from "../ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

export function InvitesTable() {
	const { data } = useGetInvites();
	const { data: groups } = useGetGroups();

	if (!data || !groups) {
		return <p>No invites</p>;
	}

	const cHelper = createColumnHelper<InviteDto>();
	const columns = [
		cHelper.accessor("id", {
			header: () => "Id",
		}),
		cHelper.accessor("invite_code", {
			header: () => "Invite Code",
			cell: (row) => {
				const inviteCode = row.getValue();
				const inviteType = row.row.original.type;
				const path = inviteType === "internal" ? "register" : "invite";
				const inviteUrl = `https://ied-admin.ldlabs.diy/${path}/${inviteCode}`;

				return (
					<span className="inline-flex gap-x-4 items-center">
						<span>{inviteCode}</span>
						<Tooltip>
							<TooltipTrigger
								render={
									<Button
										onClick={() => {
											copyToClipboard(inviteUrl);
											toast("Copied invite to clipboard");
										}}
										variant="outline"
										size="icon"
									>
										<Copy />
									</Button>
								}
							></TooltipTrigger>
							<TooltipContent>Click to copy invite link</TooltipContent>
						</Tooltip>
					</span>
				);
			},
		}),
		cHelper.accessor("accepted", {
			header: () => "Accepted",
			cell: (props) => {
				return props.getValue() ? "Yes" : "No";
			},
		}),
		cHelper.accessor("type", {
			header: () => "Type",
			cell: (props) => {
				const type = props.getValue();
				return (
					<Badge variant={type === "external" ? "secondary" : "default"}>
						{" "}
						{type.charAt(0).toUpperCase() + type.slice(1)}
					</Badge>
				);
			},
		}),
		cHelper.accessor("groups", {
			header: () => "Groups",
			cell: (props) => {
				const inviteGroups = props.getValue();
				if (!inviteGroups || inviteGroups.length === 0) {
					return "-";
				}
				return inviteGroups
					.map((g) => groups.find((x) => x.group_name === g)?.abbreviation)
					.join(", ");
			},
		}),
		cHelper.accessor("expires", {
			header: () => "Expires",
			cell: (row) => (
				<div>
					<p>{new Date(row.getValue()).toLocaleDateString()}</p>
					<p>{new Date(row.getValue()).toLocaleTimeString()}</p>
				</div>
			),
		}),
		cHelper.accessor("created", {
			header: () => "Created",
			cell: (row) => (
				<div>
					<p>{new Date(row.getValue()).toLocaleDateString()}</p>
					<p>{new Date(row.getValue()).toLocaleTimeString()}</p>
				</div>
			),
		}),
		cHelper.accessor("updated", {
			header: () => "Updated",
			cell: (row) => (
				<div>
					<p>{new Date(row.getValue()).toLocaleDateString()}</p>
					<p>{new Date(row.getValue()).toLocaleTimeString()}</p>
				</div>
			),
		}),
	];

	const tableOptions: TableOptions<InviteDto> = {
		columns,
		data,
		getCoreRowModel: getCoreRowModel(),
	};
	return (
		<>
			<TanstackTable options={tableOptions} />
		</>
	);
}

async function copyToClipboard(text: string) {
	try {
		await navigator.clipboard.writeText(text);
		console.log("Copied to clipboard!");
	} catch (err) {
		console.error("Failed to copy:", err);
	}
}
